import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  useDrawer,
  Conditional,
  useBreakpoint
} from '@cat-ecom/pcc-components';

import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { CatRadioField } from 'blocks-nextjs/blocks-react/components/RadioField';
import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { CatIconArrowBack } from 'blocks-nextjs/blocks-react/components/Icons/ArrowBack';
import { useTranslations } from 'next-intl';
import { CatFieldset } from 'blocks-nextjs/blocks-react/components/Fieldset';
import { CustomerNumbersInfo } from 'services/dealer-association/types';
import { useFormContext } from 'react-hook-form';
import { ViewResultsButton } from '../common/view-results-button';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { STATUS_SUCCESS } from 'constants/analyticsConstants';
import {
  FILTER_BY_ACCOUNT_FORM_CONTENT,
  FILTER_BY_ACCOUNT_FORM_LOCATION,
  FILTER_BY_ACCOUNT_FORM_NAME
} from 'components/notifications/constants';
import { ClearFiltersButton } from '../common/clear-filters-button';
import { PAGE_BREAKPOINTS } from 'constants/common';

interface FilterByAccountProps {
  accountsDrawer: ReturnType<typeof useDrawer>;
  customerNumbers: CustomerNumbersInfo[];
  notificationsCount: number;
  filterDrawer: ReturnType<typeof useDrawer>;
}

export const FilterByAccount = ({
  accountsDrawer,
  customerNumbers,
  notificationsCount,
  filterDrawer
}: FilterByAccountProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const { watch, setValue } = useFormContext();
  const currentFilterAccount = watch('filterByAccount');
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const gaEventContent = {
    formName: FILTER_BY_ACCOUNT_FORM_NAME,
    formContent: `${FILTER_BY_ACCOUNT_FORM_CONTENT} ${currentFilterAccount}`,
    formLocation: FILTER_BY_ACCOUNT_FORM_LOCATION,
    formStatus: STATUS_SUCCESS
  };

  const closeDrawers = () => {
    if (currentFilterAccount) {
      fireEvent(gaEventContent);
    }
    accountsDrawer.closeDrawer();
    filterDrawer.closeDrawer();
  };

  const handleBackButton = () => {
    if (currentFilterAccount) {
      fireEvent(gaEventContent);
    }
    accountsDrawer.closeDrawer();
  };

  const handleClearFilterBtn = () => {
    setValue('filterByAccount', '');
  };

  return (
    <Drawer {...accountsDrawer.drawerProps} onBlDrawerClose={closeDrawers}>
      <DrawerHeader className={'d-flex align-items-center text-capitalize'}>
        <CatButton
          className="me-2"
          hideText
          onClick={handleBackButton}
          variant="ghost"
          data-testid="filter-by-account-back-button"
        >
          <CatIconArrowBack slot="before" />
        </CatButton>
        <CatHeading
          variant="title"
          slot="header"
          className="cat-u-theme-typography-title-sm"
        >
          {`${t('LN_SEARCH_FACET_FILTER_BY')}: ${t('FOOTER_ACCOUNT')}`}
        </CatHeading>
      </DrawerHeader>
      <DrawerBody className="p-3 ps-4">
        <CatFieldset>
          {customerNumbers?.map((customerNumber) => (
            <CatRadioField
              key={customerNumber.value}
              name={customerNumber.name}
              onBlRadioChange={() =>
                setValue('filterByAccount', customerNumber.name)
              }
              value={customerNumber.name}
              checked={currentFilterAccount === customerNumber.name}
            >
              {customerNumber.name}
            </CatRadioField>
          ))}
        </CatFieldset>
      </DrawerBody>
      <DrawerFooter>
        <ViewResultsButton
          onClick={closeDrawers}
          count={notificationsCount}
          fullWidth={!isMD}
        />
        <Conditional test={currentFilterAccount}>
          <ClearFiltersButton
            onClick={handleClearFilterBtn}
            fullWidth={!isMD}
          />
        </Conditional>
      </DrawerFooter>
    </Drawer>
  );
};
